// NodeTypeLabels.js

const DEFAULT_SINGULAR_FALLBACK = 'Node';
const DEFAULT_PLURAL_FALLBACK = 'Items';

export function normalizeNodeTypeKey(typeKey) {
  return String(typeKey || '').trim();
}

export function buildNodeTypeDefinitionMap(types) {
  const map = new Map();
  (Array.isArray(types) ? types : []).forEach((typeDef) => {
    const key = normalizeNodeTypeKey(typeDef?.key);
    if (!key) return;
    map.set(key, typeDef);
  });
  return map;
}

export function getNodeTypeDefinitionFromMap(typeMap, typeKey) {
  const normalized = normalizeNodeTypeKey(typeKey);
  if (!normalized || !(typeMap instanceof Map)) return null;
  return typeMap.get(normalized) || null;
}

export function resolveNodeTypeDefinition(getType, typeKey) {
  if (typeof getType !== 'function') return null;

  const normalized = normalizeNodeTypeKey(typeKey);
  if (!normalized) return null;

  const variants = [
    normalized,
    normalized.toUpperCase(),
    normalized.toLowerCase(),
  ];

  for (const variant of variants) {
    const typeDef = getType(variant);
    if (typeDef) return typeDef;
  }

  return null;
}

export function getNodeTypeLabelFromDefinition(
  typeDef,
  typeKey,
  { fallback = DEFAULT_SINGULAR_FALLBACK } = {}
) {
  const label = String(typeDef?.label || '').trim();
  if (label) return label;

  return fallback;
}

export function getNodeTypePluralLabelFromDefinition(
  typeDef,
  typeKey,
  { fallback = DEFAULT_PLURAL_FALLBACK } = {}
) {
  const plural = String(typeDef?.labelPlural || '').trim();
  if (plural) return plural;

  const singular = getNodeTypeLabelFromDefinition(typeDef, typeKey, { fallback: '' });
  if (singular) return singular;

  return fallback;
}

export function getNodeTypeLabel(
  getType,
  typeKey,
  { fallback = DEFAULT_SINGULAR_FALLBACK } = {}
) {
  const typeDef = resolveNodeTypeDefinition(getType, typeKey);
  return getNodeTypeLabelFromDefinition(typeDef, typeKey, { fallback });
}

export function getNodeTypePluralLabel(
  getType,
  typeKey,
  { fallback = DEFAULT_PLURAL_FALLBACK } = {}
) {
  const typeDef = resolveNodeTypeDefinition(getType, typeKey);
  return getNodeTypePluralLabelFromDefinition(typeDef, typeKey, { fallback });
}

export function formatNodeTypeOptionText(
  typeDef,
  typeKey,
  { fallback = 'No label' } = {}
) {
  const normalizedKey = normalizeNodeTypeKey(typeKey || typeDef?.key);
  return getNodeTypePluralLabelFromDefinition(typeDef, normalizedKey, { fallback });
}

export function compareNodeTypeOptionText(aTypeDef, bTypeDef, { fallback = 'No label' } = {}) {
  const aText = formatNodeTypeOptionText(aTypeDef, aTypeDef?.key, { fallback });
  const bText = formatNodeTypeOptionText(bTypeDef, bTypeDef?.key, { fallback });
  return aText.localeCompare(bText);
}
